import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { UserStatus } from '../../types/auth';

interface StatCard {
    title: string;
    value: string;
    change?: string;
    changeType?: 'positive' | 'negative' | 'neutral';
    icon: React.ReactNode;
    color: string;
}

interface QuickAction {
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    onClick: () => void;
    disabled?: boolean;
}

interface DashboardStats {
    activeOrders: number;
    contracts: number;
    totalOrdersSum: number;
    recentChange: number;
}

export const ClientDashboard: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);

    // Загрузка статистики из БД
    useEffect(() => {
        if (!user) return;
        const fetchDashboardStats = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
                const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

                const response = await fetch(`${API_BASE_URL}/orders/dashboard-stats`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data) {
                        setStats(result.data);
                    }
                } else {
                    console.error('Ошибка получения статистики дашборда');
                }
            } catch (error) {
                console.error('Ошибка загрузки статистики:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardStats();
    }, [user]);

    // Проверка пользователя - ранний выход
    if (!user) return null;

    const isFullyActive = user.status === UserStatus.APPROVED || user.status === UserStatus.VERIFIED;

    // Форматирование суммы в тенге
    const formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'KZT',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount).replace('KZT', '₸');
    };

    const statCards: StatCard[] = [
        {
            title: 'Активные заявки',
            value: loading ? '...' : String(stats?.activeOrders || 0),
            change: loading ? undefined : (stats?.activeOrders && stats.activeOrders > 0 ? `+${stats.activeOrders}` : undefined),
            changeType: 'positive',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
            ),
            color: 'blue'
        },
        {
            title: 'Договоры',
            value: loading ? '...' : String(stats?.contracts || 0),
            change: loading ? undefined : (stats?.contracts && stats.contracts > 0 ? String(stats.contracts) : '0'),
            changeType: 'neutral',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            color: 'green'
        },
        {
            title: 'Сумма заказов',
            value: loading ? '...' : formatCurrency(stats?.totalOrdersSum || 0),
            change: loading ? undefined : (stats?.recentChange ? `+${formatCurrency(stats.recentChange)}` : undefined),
            changeType: 'positive',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
            ),
            color: 'purple'
        },
        {
            title: 'Статус аккаунта',
            value: user.status === UserStatus.VERIFIED ? 'Верифицирован' :
                user.status === UserStatus.APPROVED ? 'Одобрен' :
                    user.status === UserStatus.PENDING_MODERATION ? 'На модерации' :
                        'Требует действий',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            color: user.status === UserStatus.VERIFIED || user.status === UserStatus.APPROVED ? 'green' :
                user.status === UserStatus.PENDING_MODERATION ? 'yellow' : 'red'
        }
    ];

    const quickActions: QuickAction[] = [
        {
            title: 'Создать заявку',
            description: 'Оформить новую заявку на покупку продукции',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
            ),
            color: 'blue',
            onClick: () => navigate('/client/orders/create'),
            disabled: !isFullyActive
        },
        {
            title: 'Заявка на модерацию',
            description: 'Отследить статус рассмотрения заявки',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            color: user.status === UserStatus.NEEDS_REVISION ? 'orange' :
                user.status === UserStatus.PENDING_MODERATION ? 'yellow' :
                    user.status === UserStatus.APPROVED || user.status === UserStatus.VERIFIED ? 'green' : 'blue',
            onClick: () => navigate('/client/moderation')
        },
        {
            title: 'Документы',
            description: 'Загрузить и управлять документами',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            ),
            color: 'indigo',
            onClick: () => navigate('/client/documents')
        },
        {
            title: 'Профиль',
            description: 'Управление личными данными и сотрудниками',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
            color: 'purple',
            onClick: () => navigate('/client/profile')
        }
    ];

    const getColorClasses = (color: string) => {
        const colorMap = {
            blue: {
                bg: 'bg-blue-50',
                icon: 'bg-blue-100 text-blue-600',
                text: 'text-blue-600',
                border: 'border-blue-200'
            },
            green: {
                bg: 'bg-green-50',
                icon: 'bg-green-100 text-green-600',
                text: 'text-green-600',
                border: 'border-green-200'
            },
            purple: {
                bg: 'bg-purple-50',
                icon: 'bg-purple-100 text-purple-600',
                text: 'text-purple-600',
                border: 'border-purple-200'
            },
            indigo: {
                bg: 'bg-indigo-50',
                icon: 'bg-indigo-100 text-indigo-600',
                text: 'text-indigo-600',
                border: 'border-indigo-200'
            },
            yellow: {
                bg: 'bg-yellow-50',
                icon: 'bg-yellow-100 text-yellow-600',
                text: 'text-yellow-600',
                border: 'border-yellow-200'
            },
            red: {
                bg: 'bg-red-50',
                icon: 'bg-red-100 text-red-600',
                text: 'text-red-600',
                border: 'border-red-200'
            },
            orange: {
                bg: 'bg-orange-50',
                icon: 'bg-orange-100 text-orange-600',
                text: 'text-orange-600',
                border: 'border-orange-200'
            }
        };
        return colorMap[color as keyof typeof colorMap] || colorMap.blue;
    };

    return (
        <div className="space-y-6">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-blue-50 via-white to-indigo-50 rounded-2xl border border-blue-100 p-8">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <span className="text-2xl">👋</span>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Добро пожаловать, {user.fullName}!
                            </h1>
                            <p className="text-gray-600 mt-1">
                                {isFullyActive
                                    ? 'Ваш аккаунт активен. Вы можете создавать заявки и управлять договорами.'
                                    : 'Завершите процесс верификации для получения полного доступа к функциям.'
                                }
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-500">Сегодня</p>
                        <p className="text-lg font-semibold text-gray-900">
                            {new Date().toLocaleDateString('ru-RU', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((card, index) => {
                    const colors = getColorClasses(card.color);
                    return (
                        <div key={index} className={`${colors.bg} rounded-xl border ${colors.border} p-6 transition-all duration-200 hover:shadow-lg`}>
                            <div className="flex items-center justify-between">
                                <div className={`w-12 h-12 ${colors.icon} rounded-lg flex items-center justify-center`}>
                                    {card.icon}
                                </div>
                                {card.change && (
                                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${card.changeType === 'positive' ? 'bg-green-100 text-green-600' :
                                        card.changeType === 'negative' ? 'bg-red-100 text-red-600' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                        {card.change}
                                    </span>
                                )}
                            </div>
                            <div className="mt-4">
                                <h3 className="text-sm font-medium text-gray-600">{card.title}</h3>
                                <p className={`text-2xl font-bold ${colors.text} mt-1`}>{card.value}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Быстрые действия</h2>
                    <span className="text-sm text-gray-500">Выберите действие для продолжения</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {quickActions.map((action, index) => {
                        const colors = getColorClasses(action.color);
                        return (
                            <button
                                key={index}
                                onClick={action.onClick}
                                disabled={action.disabled}
                                className={`group relative p-6 rounded-xl border-2 border-dashed transition-all duration-200 text-left ${action.disabled
                                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                    : `${colors.border} hover:${colors.bg} hover:border-solid hover:shadow-md`
                                    }`}
                            >
                                <div className={`w-12 h-12 ${action.disabled ? 'bg-gray-100 text-gray-400' : colors.icon} rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-105`}>
                                    {action.icon}
                                </div>
                                <h3 className={`font-semibold mb-2 ${action.disabled ? 'text-gray-400' : colors.text}`}>
                                    {action.title}
                                </h3>
                                <p className={`text-sm ${action.disabled ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {action.description}
                                </p>
                                {action.disabled && (
                                    <div className="absolute top-3 right-3">
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Последняя активность</h2>
                    <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                        Показать все
                    </button>
                </div>
                <div className="space-y-4">
                    {[
                        {
                            icon: '📋',
                            title: 'Создана заявка #ORD-2024-001',
                            time: '2 часа назад',
                            status: 'success'
                        },
                        {
                            icon: '📄',
                            title: 'Подписан договор #CON-2024-005',
                            time: '1 день назад',
                            status: 'success'
                        },
                        {
                            icon: '🔄',
                            title: 'Обновлены данные компании',
                            time: '3 дня назад',
                            status: 'info'
                        }
                    ].map((activity, index) => (
                        <div key={index} className="flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                <span className="text-lg">{activity.icon}</span>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                                <p className="text-xs text-gray-500">{activity.time}</p>
                            </div>
                            <div className={`w-2 h-2 rounded-full ${activity.status === 'success' ? 'bg-green-400' :
                                activity.status === 'info' ? 'bg-blue-400' : 'bg-gray-400'
                                }`}></div>
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
};
